// DEBT-VITA™ — Panneau d'analyse narrative LLM (cached)
// 1 appel GPT-4o au plus, mise en cache backend, bouton de régénération.
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Sparkles,
  AlertTriangle,
  Quote,
  Lightbulb,
  RefreshCw,
  History,
  CheckCircle2,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  generateDebtNarrative,
  clearDebtNarrative,
} from "@/lib/debt";

function formatDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

export default function DebtNarrativePanel({
  projectId,
  hasResults,
  onIncludeInReport,
  initialNarrative,
}) {
  const [narrative, setNarrative] = useState(initialNarrative?.narrative || null);
  const [generatedAt, setGeneratedAt] = useState(initialNarrative?.generated_at || null);
  const [fromCache, setFromCache] = useState(!!initialNarrative);
  const [loading, setLoading] = useState(false);
  const [included, setIncluded] = useState(false);

  // Auto-load existing narrative on mount
  useEffect(() => {
    if (!hasResults || initialNarrative) return;
    let cancelled = false;
    async function load() {
      try {
        // Don't trigger LLM — just attempt cache hit by calling without force.
        // Backend returns from_cache=true if exists; otherwise it WILL call LLM.
        // To stay LLM-cost safe at mount, we DO NOT auto-fetch here. The user
        // must explicitly click "Générer" the first time.
        // (We could add a dedicated GET endpoint later for cache-only reads.)
      } catch {
        /* ignore */
      } finally {
        if (!cancelled) {
          /* noop */
        }
      }
    }
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasResults, projectId]);

  async function handleGenerate(force = false) {
    setLoading(true);
    try {
      const data = await generateDebtNarrative(projectId, force);
      if (data) {
        setNarrative(data.narrative);
        setGeneratedAt(data.generated_at);
        setFromCache(!!data.from_cache);
        toast.success(
          data.from_cache
            ? "Analyse chargée depuis le cache."
            : "Analyse narrative générée."
        );
      }
    } catch (e) {
      const msg =
        e?.response?.data?.detail || e?.message || "Échec de la génération.";
      toast.error(typeof msg === "string" ? msg : "Échec.");
    } finally {
      setLoading(false);
    }
  }

  async function handleRegenerate() {
    if (
      !window.confirm(
        "Cela effacera l'analyse actuelle et déclenchera un nouvel appel au modèle GPT-4o (coût LLM). Continuer ?"
      )
    ) {
      return;
    }
    setLoading(true);
    try {
      await clearDebtNarrative(projectId);
      const data = await generateDebtNarrative(projectId, true);
      if (data) {
        setNarrative(data.narrative);
        setGeneratedAt(data.generated_at);
        setFromCache(false);
        setIncluded(false);
        toast.success("Analyse régénérée.");
      }
    } catch (e) {
      toast.error("Échec de la régénération.");
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  if (!hasResults) {
    return null;
  }

  if (!narrative) {
    return (
      <Card className="border-blue-300/50 bg-gradient-to-br from-blue-50/50 to-amber-50/30 dark:from-blue-950/20 dark:to-amber-950/10" data-testid="debt-narrative-empty">
        <CardContent className="flex flex-col items-start gap-3 p-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-bold">
              Analyse narrative vitaliste de la dette
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Générez une lecture existentielle et juridique de la situation
              d'endettement (1 appel GPT-4o, ~5–10 s, mise en cache).
            </p>
          </div>
          <Button
            onClick={() => handleGenerate(false)}
            disabled={loading}
            className="bg-blue-700 text-white hover:bg-blue-800"
            data-testid="debt-narrative-generate-btn"
          >
            {loading ? (
              <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="mr-2 h-4 w-4" />
            )}
            Générer l'analyse narrative
          </Button>
        </CardContent>
      </Card>
    );
  }

  const apg = narrative.analyse_par_generation || {};

  return (
    <Card data-testid="debt-narrative-panel" className="overflow-hidden">
      <CardHeader className="bg-gradient-to-r from-blue-50 to-amber-50 dark:from-blue-950/20 dark:to-amber-950/20">
        <CardTitle className="text-lg text-blue-800 dark:text-blue-300">
          {narrative.titre_analyse || "Analyse vitaliste de la dette"}
        </CardTitle>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <History className="h-3 w-3" />
            Générée le {formatDate(generatedAt) || "—"}
          </span>
          {fromCache ? (
            <span
              data-testid="debt-narrative-cache-badge"
              className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
            >
              <CheckCircle2 className="h-3 w-3" />
              Lue du cache (zéro nouvel appel LLM)
            </span>
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="space-y-6 p-5">
        {/* Synthèse */}
        <section>
          <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
            Synthèse vitaliste
          </h3>
          <p className="mt-2 text-base leading-relaxed">
            {narrative.synthese_vitaliste}
          </p>
        </section>

        {/* Analyse par génération */}
        <section className="grid grid-cols-1 gap-3 md:grid-cols-3" data-testid="debt-narr-generations">
          <div className="rounded-lg border border-border bg-muted/30 p-3">
            <p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
              Génération actuelle
            </p>
            <p className="mt-2 text-sm leading-relaxed">
              {apg.generation_actuelle}
            </p>
          </div>
          <div className="rounded-lg border border-border bg-muted/30 p-3">
            <p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
              Génération future
            </p>
            <p className="mt-2 text-sm leading-relaxed">
              {apg.generation_future}
            </p>
          </div>
          <div className="rounded-lg border border-border bg-muted/30 p-3">
            <p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
              Constat intergénérationnel
            </p>
            <p className="mt-2 text-sm leading-relaxed">
              {apg.constat_intergen}
            </p>
          </div>
        </section>

        {/* Points critiques */}
        {Array.isArray(narrative.points_critiques) && narrative.points_critiques.length > 0 ? (
          <section data-testid="debt-narr-critical-points">
            <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
              Points critiques
            </h3>
            <ul className="mt-2 space-y-2">
              {narrative.points_critiques.map((p, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 rounded-md border border-amber-300/30 bg-amber-50/40 p-2 text-sm dark:bg-amber-950/20"
                >
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {/* Comparaison historique */}
        {narrative.comparaison_historique ? (
          <section>
            <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
              Comparaison historique
            </h3>
            <p className="mt-2 text-sm leading-relaxed">
              {narrative.comparaison_historique}
            </p>
          </section>
        ) : null}

        {/* Citation */}
        {narrative.citation_ouvrage ? (
          <section data-testid="debt-narr-quote">
            <blockquote className="relative rounded-lg border-l-4 border-amber-500 bg-amber-50/60 p-4 italic text-sm leading-relaxed dark:bg-amber-950/20">
              <Quote className="absolute -top-2 left-2 h-4 w-4 text-amber-600 dark:text-amber-400" />
              <span className="ml-4">{narrative.citation_ouvrage}</span>
              <footer className="mt-2 text-right text-[11px] font-semibold not-italic text-amber-700 dark:text-amber-300">
                — A. ELY Mustapha, <em>Nouvelle Théorie des Finances Publiques</em>
              </footer>
            </blockquote>
          </section>
        ) : null}

        {/* Recommandations */}
        {Array.isArray(narrative.recommandations_vitalistes) && narrative.recommandations_vitalistes.length > 0 ? (
          <section data-testid="debt-narr-recommendations">
            <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
              Recommandations vitalistes
            </h3>
            <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2">
              {narrative.recommandations_vitalistes.map((r, i) => (
                <div
                  key={i}
                  className="flex items-start gap-2 rounded-md border border-emerald-300/40 bg-emerald-50/50 p-3 text-sm dark:bg-emerald-950/20"
                >
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-[11px] font-bold text-white">
                    {i + 1}
                  </span>
                  <span className="leading-relaxed">{r}</span>
                </div>
              ))}
            </div>
          </section>
        ) : null}

        {/* Conclusion */}
        {narrative.conclusion ? (
          <section>
            <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
              Conclusion
            </h3>
            <p className="mt-2 text-sm leading-relaxed">{narrative.conclusion}</p>
          </section>
        ) : null}

        {/* Actions */}
        <section className="flex flex-wrap items-center gap-3 border-t border-border pt-4">
          <Button
            variant="outline"
            onClick={handleRegenerate}
            disabled={loading}
            data-testid="debt-narrative-regenerate-btn"
          >
            <RefreshCw className={"mr-2 h-4 w-4 " + (loading ? "animate-spin" : "")} />
            Régénérer (nouvel appel LLM)
          </Button>
          {onIncludeInReport ? (
            <Button
              variant={included ? "default" : "secondary"}
              onClick={() => {
                setIncluded(true);
                onIncludeInReport(narrative);
                toast.success("L'analyse sera incluse dans le rapport.");
              }}
              disabled={included}
              data-testid="debt-narrative-include-in-report-btn"
            >
              <Lightbulb className="mr-2 h-4 w-4" />
              {included ? "Incluse dans le rapport" : "Inclure dans le rapport"}
            </Button>
          ) : null}
        </section>
      </CardContent>
    </Card>
  );
}
